import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ReduxHooks from '../../store/ReduxHooks';
import { actionCreators } from '../../store/actions';
import ProfileManager from '../../services/api/ProfileManager';
import DeliveryIcon from '../../resources/assets/icons/DeliveryIcon';
import ConfirmationModal from '../../modals/ConfirmationModal';
import { DeliveryMode, DeliveryProvider } from '../../services/exports/Constants';
import UberEatsIcon from '../../resources/assets/icons/UberEatsIcon';
import WoltIcon from '../../resources/assets/icons/WoltIcon';
import { Button, Switch } from 'antd';
import CheckableTags from '../../components/lib/CheckableTags';
import Layout from '../../components/layout/Layout';
import { DateTime } from 'luxon';

const initialConfirmationModal = {
  show: false,
  title: null,
  description: null,
  onConfirm: null,
};

export default function OrderControlView() {
  const { t } = useTranslation(null, { keyPrefix: 'Views:OrderControl' });

  const { company } = useSelector((state) => state.company);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(false);

  const [confirmationModal, setConfirmationModal] = useState(initialConfirmationModal);

  async function pauseOrders() {
    setLoading(true);
    const { success, data } = await ProfileManager.pauseOrders(selectedOption.factory());
    setTimeout(() => setLoading(false), 2000);

    if (!success) {
      return toast.error(t('toasts.failed_to_save_changes'));
    }

    ReduxHooks.dispatch(actionCreators.company.set(data.data));

    return toast.success(t('toasts.changes_got_saved'));
  }

  const saveChanges = async (data) => {
    setLoading(true);
    const { success, data: responseData } = await ProfileManager.update(data);
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_save_changes'));
    }

    ReduxHooks.dispatch(actionCreators.company.set(responseData.data));
    setConfirmationModal(initialConfirmationModal);

    return toast.success(t('toasts.changes_got_saved'));
  };

  async function toggleDeliveryEnabled(value, confirmed = false) {
    if (!confirmed) {
      return setConfirmationModal({
        show: true,
        title: t(`modals.toggle_delivery.${value}.title`),
        description: t(`modals.toggle_delivery.${value}.description`),
        onConfirm: () => toggleDeliveryEnabled(value, true),
      });
    }

    return saveChanges({ delivery_enabled: value });
  }

  async function switchDeliveryMode(value, confirmed = false) {
    if (!confirmed) {
      return setConfirmationModal({
        show: true,
        title: t(`modals.switch_delivery_mode.${value}.title`, { provider: company?.service_provider?.name }),
        description: t(`modals.switch_delivery_mode.${value}.description`, {
          provider: company?.service_provider?.name,
        }),
        onConfirm: () => switchDeliveryMode(value, true),
      });
    }

    return saveChanges({ delivery_mode: value });
  }

  const options = [
    {
      id: 0,
      label: t('form.orders_paused_until.options.10_min'),
      factory: () => DateTime.local().plus({ minutes: 10 }),
    },
    {
      id: 1,
      label: t('form.orders_paused_until.options.15_min'),
      factory: () => DateTime.local().plus({ minutes: 15 }),
    },
    {
      id: 2,
      label: t('form.orders_paused_until.options.20_min'),
      factory: () => DateTime.local().plus({ minutes: 20 }),
    },
    {
      id: 3,
      label: t('form.orders_paused_until.options.25_min'),
      factory: () => DateTime.local().plus({ minutes: 25 }),
    },
    {
      id: 4,
      label: t('form.orders_paused_until.options.1_hour'),
      factory: () => DateTime.local().plus({ hours: 1 }),
    },
    {
      id: 5,
      label: t('form.orders_paused_until.options.2_hours'),
      factory: () => DateTime.local().plus({ hours: 2 }),
    },
    {
      id: 6,
      label: t('form.orders_paused_until.options.rest_of_day'),
      factory: () => DateTime.local().endOf('day'),
    },
  ];

  const renderOrderMethods = useMemo(() => {
    if (!company?.has_delivery) {
      return null;
    }

    return (
      <div className="space-y-md">
        <hr />
        <h4>{t('sections.order_methods.title')}</h4>
        <p>{t('sections.order_methods.description')}</p>
        {company?.has_delivery && (
          <div className="flex gap-xs">
            <Switch
              id="delivery-enabled"
              className="my-auto"
              checked={company?.delivery_enabled}
              onChange={(checked) => toggleDeliveryEnabled(checked)}
            />
            <label htmlFor="delivery-enabled" className="flex items-center my-auto gap-xs">
              <div className="h-7 w-7 bg-gray-800 rounded-full flex mr-2">
                <DeliveryIcon className="h-6 w-6 m-auto" />
              </div>
              <span className="my-auto">
                {company?.delivery_enabled ? t('labels.delivery_orders_on') : t('labels.delivery_orders_off')}
              </span>
            </label>
          </div>
        )}
        {company?.has_delivery && company?.supports_internal_delivery && company?.supports_external_delivery && (
          <div className="space-y-md">
            <hr />
            <h4>{t('sections.delivery_mode.title')}</h4>
            <p>{t('sections.delivery_mode.description')}</p>
            <div className="flex gap-xs">
              <Switch
                id="has_standalone_delivery"
                className="my-auto"
                checked={company?.delivery_mode === DeliveryMode.Internal}
                onChange={(checked) => checked && switchDeliveryMode(DeliveryMode.Internal)}
              />
              <label htmlFor="has_standalone_delivery" className="my-auto flex">
                <div className="h-7 w-7 bg-gray-800 rounded-full flex mr-2">
                  <DeliveryIcon className="h-6 w-6 m-auto" />
                </div>
                <span className="my-auto">{t('form.has_standalone_delivery.label')}</span>
              </label>
            </div>
            <div className="flex gap-xs">
              <Switch
                id="has_external_delivery"
                className="my-auto"
                checked={company?.delivery_mode === DeliveryMode.External}
                onChange={(checked) => checked && switchDeliveryMode(DeliveryMode.External)}
              />
              <label htmlFor="has_external_delivery" className="flex my-auto items-center cursor-pointer">
                {company?.supported_delivery_providers?.includes(DeliveryProvider.WoltDrive) && (
                  <WoltIcon className="h-7 w-7 mr-2" />
                )}
                {(company?.supported_delivery_providers?.includes(DeliveryProvider.FirstDelivery) ||
                  company?.supported_delivery_providers?.includes(DeliveryProvider.UberDrive)) && (
                  <UberEatsIcon className="h-7 w-7 mr-2" />
                )}
                {t('form.has_external_delivery.label', { provider: company?.service_provider?.name })}
              </label>
            </div>
          </div>
        )}
      </div>
    );
  }, [company]);

  return (
    <Layout.Page className="space-y-md">
      <h4>{t('sections.pause_orders.title')}</h4>
      <p>{t('sections.pause_orders.description')}</p>
      <CheckableTags options={options} selectedOption={selectedOption} onChange={setSelectedOption} />
      <Button
        type="primary"
        onClick={pauseOrders}
        disabled={selectedOption === null}
        loading={loading}
        className="w-full"
      >
        {t('buttons.confirm')}
      </Button>
      {renderOrderMethods}
      <ConfirmationModal
        show={confirmationModal.show}
        title={confirmationModal.title}
        description={confirmationModal.description}
        cancelButtonText={t('buttons.cancel')}
        confirmationButtonText={t('buttons.confirm')}
        onConfirm={confirmationModal.onConfirm}
        onCancel={() => setConfirmationModal(initialConfirmationModal)}
        confirming={loading}
      />
    </Layout.Page>
  );
}
